import { useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { delugeIdAtom, UserStatusEmoji } from '@root/entities/auth'
import { UserShort } from '@root/entities/user-short'
import { UserShortAvatar } from '@root/entities/user-short/ui/user-short-avatar'
import { ProfileLink } from '@root/features/profile'
import { useTranslation } from '@root/shared/localization'
import { CopyButton, Truncate, type Column } from '@root/shared/ui-kit'

import { BackOfficeUsersActivityToggleButton } from '../back-office-users-activity-toggle-button'
import { FilterByActivity } from '../filters/filter-by-activity'
import { FilterByName } from '../filters/filter-by-name'

import { DateCell } from './date-cell'
import { TwoRowsTextCell } from './two-rows-text-cell'
import { WalletsCell } from './wallets-cell'

import type { BackOfficeUser } from '@root/entities/back-office'

export const BackOfficeUsersTableColumn = {
  Avatar: {
    id: 'avatar',
    header: () => '',
    render: ({ value }) => (
      <TwoRowsTextCell
        firstRow={
          <ProfileLink delugeId={value.delugeId}>
            <UserShortAvatar userShort={value} size={25} withPreview />
          </ProfileLink>
        }
        secondRow={<></>}
      />
    ),
    width: 35,
  },
  Id: {
    id: 'id',
    header: () => 'Account id',
    render: ({ value }) => (
      <TwoRowsTextCell
        firstRow={
          <IdCell>
            <CopyButton value={value.delugeId} />
            <Truncate start={6} end={8}>
              {value.delugeId}
            </Truncate>
          </IdCell>
        }
        secondRow={undefined}
      />
    ),
    minWidth: 150,
  },
  Name: {
    id: 'name',
    header: ({ filter, setColumnFilter, clearColumnFilter, columnId }) => (
      <FilterByName
        filter={filter}
        setColumnFilter={setColumnFilter}
        clearColumnFilter={clearColumnFilter}
        columnId={columnId}
      />
    ),
    render: ({ value }) => (
      <TwoRowsTextCell
        firstRow={
          <ProfileLink delugeId={value.delugeId}>
            <Username>{value.username || '—'}</Username>
          </ProfileLink>
        }
        secondRow={
          <ProfileLink delugeId={value.delugeId}>
            <Username>{UserShort.fullName(value) || '—'}</Username>
          </ProfileLink>
        }
      />
    ),
    minWidth: 150,
  },
  FirstName: {
    id: 'first_name',
    header: ({ t }) => t('common.first_name'),
    render: ({ value }) => (
      <TwoRowsTextCell firstRow={value.firstName || '—'} secondRow={<></>} />
    ),
    minWidth: 150,
  },
  LastName: {
    id: 'last_name',
    header: ({ t }) => t('common.last_name'),
    render: ({ value }) => (
      <TwoRowsTextCell firstRow={value.lastName || '—'} secondRow={<></>} />
    ),
    minWidth: 150,
  },
  Status: {
    id: 'status',
    header: ({ t }) => t('common.status'),
    render: ({ value }) => (
      <TwoRowsTextCell
        firstRow={[UserStatusEmoji[value.status], value.status].join(' ')}
        secondRow={<></>}
      />
    ),
    width: 90,
  },
  UpdatedAt: {
    id: 'updated_at',
    header: ({ t }) => t('common.updated_at'),
    render: ({ value }) =>
      value.updatedAt ? <DateCell date={value.updatedAt} /> : undefined,
    minWidth: 150,
  },
  Activity: {
    id: 'activity',
    render: ({ value }) => <ActivityCell value={value} />,
    header: ({ filter, setColumnFilter, clearColumnFilter, columnId }) => (
      <FilterByActivity
        filter={filter}
        setColumnFilter={setColumnFilter}
        clearColumnFilter={clearColumnFilter}
        columnId={columnId}
      />
    ),

    minWidth: 120,
  },
  CreatedAt: {
    id: 'created_at',
    header: ({ t }) => t('common.created_at'),
    render: ({ value }) =>
      value.createdAt ? <DateCell date={value.createdAt} /> : undefined,
    minWidth: 150,
  },
  Wallets: {
    id: 'wallets',
    header: ({ t }) => t('back-office.table.wallets'),
    render: ({ value }) => <WalletsCell user={value} />,
    minWidth: 150,
  },
} satisfies Record<string, Column<BackOfficeUser.BackOfficeUser>>

const Username = styled.span`
  color: var(--text-11);
`
const ActivityCell = (props: { value: BackOfficeUser.BackOfficeUser }) => {
  const { t } = useTranslation()
  const [blocked] = useAtom(props.value.blocked)
  const [delugeId] = useAtom(delugeIdAtom)
  return (
    <TwoRowsTextCell
      firstRow={
        <Root>
          <span>
            {[
              blocked ? '⛔' : '✅',
              blocked
                ? t('back_office.user.blocked')
                : t('back_office.user.active'),
            ].join(' ')}
          </span>
          {delugeId === props.value.delugeId ? undefined : (
            <BackOfficeUsersActivityToggleButton backOfficeUser={props.value} />
          )}
        </Root>
      }
      secondRow={<></>}
    />
  )
}

const Root = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`
const IdCell = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`
